/**
 * Institutional style registry for the world-class CFO forecast Excel workbook.
 *
 * Every cell role (titles, headers, input cells, formula cells, cross-sheet links,
 * subtotals, totals, KPI tiles, forecast-period shading, etc.) maps to a single
 * deduplicated style id built through the engine's `buildSpreadsheetStyleFromFormat`
 * (the same helper the legacy export already uses). Centralising the palette and
 * number formats here keeps the whole workbook visually consistent and makes a
 * brand/theme change a one-line edit.
 *
 * Color code (mirrors the institutional convention shown in the Cover legend):
 *   blue  = hard input          (editable driver cells)
 *   ink   = in-sheet formula     (calculated)
 *   green = cross-sheet link
 *   amber = forecast period band (vs actual)
 */
import { buildSpreadsheetStyleFromFormat } from '../../../protocol/spreadsheetEngineProtocol'

/**
 * Institutional design palette — "petrol + brass on ivory". Restrained, editorial,
 * built for a financial statement that should read like a top-tier advisory firm
 * produced it. Ivory paper + warm ink, a single confident petrol accent, a thin
 * brass rule for brand presence, hairline rules instead of boxes, and semantic
 * colour only where it carries meaning. Hex strings; the engine normalises to ARGB.
 */
export const CFO_PALETTE = {
  ink: '#1B1F24',          // warm near-black — primary text + figures
  subtle: '#6E7178',       // secondary text
  faint: '#9A958A',        // warm gray — hints, units, muted labels
  inputBlue: '#185FA5',    // editable inputs
  inputFill: '#EAF1F8',
  linkGreen: '#0C4A4E',    // cross-sheet links use the petrol accent
  forecastText: '#9A7B3F', // warm brass-brown — quiet forecast emphasis (not loud amber)
  forecastFill: '#F7F2E9', // soft warm tint for forecast columns
  actualText: '#42454B',
  actualFill: '#FCFBF8',   // actuals sit on the ivory paper
  headerText: '#6E7178',   // muted, lowercase-feel column headers
  headerFill: '#FCFBF8',
  sectionText: '#1B1F24',
  sectionFill: '#F1EEE6',  // warm section band
  subtotalFill: '#F4F2EC', // very soft subtotal band
  totalText: '#1B1F24',
  border: '#E8E5DE',       // hairline
  borderStrong: '#C9C5BB', // stronger hairline (subtotal rule)
  positive: '#1D6F4C',
  negative: '#9E3030',
  warnFill: '#F7EEDA',
  warnText: '#9A7B3F',
  dangerFill: '#F6E3E0',
  dangerText: '#9E3030',
  okFill: '#E1F0E7',
  okText: '#1D6F4C',
  brand: '#0C4A4E',        // petrol accent
  brandFill: '#0C4A4E',
  brandText: '#F3F1EA',    // warm white on petrol
  bandStart: '#E6F0EC',
  paper: '#FCFBF8',        // ivory page
  brass: '#B08D57',        // warm metal — brand kicker + thin rules
  grossFill: '#EEF1EC',    // soft green tint for the headline subtotal (gross profit)
} as const

/** Typeface pairing — installed everywhere Excel runs (no font embedding needed). */
export const CFO_FONTS = {
  serif: 'Georgia',        // editorial titles + grand totals (high-contrast, refined)
  sans: 'Calibri',         // data + labels (tabular figures)
} as const

/** Reusable OOXML number-format codes. `cur` is parameterised by currency symbol. */
export const cfoNumberFormats = (currencySymbol = '$') => ({
  currency: `${currencySymbol}#,##0;(${currencySymbol}#,##0);"–"`,
  currencyCents: `${currencySymbol}#,##0.00;(${currencySymbol}#,##0.00);"–"`,
  thousands: `${currencySymbol}#,##0,"k";(${currencySymbol}#,##0,"k");"–"`,
  plain: '#,##0;(#,##0);"–"',
  percent: '0.0%;(0.0%)',
  percentSigned: '+0.0%;(0.0%)',
  multiple: '0.0"×"',
  days: '0" d"',
  months: '0.0" mo"',
  weeks: '0.0" wk"',
  integer: '0',
  date: 'd-mmm-yyyy',
  monthYear: 'mmm yyyy',
})

export type CfoWorkbookStyleRole =
  | 'coverTitle'
  | 'coverSubtitle'
  | 'coverMeta'
  | 'brandBand'
  | 'legend'
  | 'sectionHeader'
  | 'columnHeader'
  | 'columnHeaderRight'
  | 'unitsRow'
  | 'rowLabel'
  | 'rowLabelIndent'
  | 'note'
  | 'inputText'
  | 'inputNumber'
  | 'inputPercent'
  | 'inputDays'
  | 'formula'
  | 'formulaPercent'
  | 'formulaMuted'
  | 'link'
  | 'actualNumber'
  | 'forecastNumber'
  | 'subtotal'
  | 'subtotalNumber'
  | 'total'
  | 'totalNumber'
  | 'kpiLabel'
  | 'kpiValue'
  | 'kpiValueLarge'
  | 'kpiUnit'
  | 'caseHigh'
  | 'caseExpected'
  | 'caseLow'

export type CfoWorkbookStyleRegistry = {
  styles: Record<string, Record<string, unknown>>
  /** Resolve a role to its deduplicated style id (or null if the role produced no style). */
  id: (role: CfoWorkbookStyleRole) => string | null
  /** Register an ad-hoc format and return its style id (deduped). */
  custom: (format: Record<string, unknown>) => string | null
  numberFormats: ReturnType<typeof cfoNumberFormats>
}

/**
 * Build the style registry for one workbook. `currencySymbol` parameterises every
 * currency number format so non-USD companies get correctly labelled numbers.
 */
export function buildCfoWorkbookStyleRegistry(currencySymbol = '$'): CfoWorkbookStyleRegistry {
  const styles: Record<string, Record<string, unknown>> = {}
  const fmt = cfoNumberFormats(currencySymbol)

  const register = (format: Record<string, unknown>): string | null => {
    const built = buildSpreadsheetStyleFromFormat(format)
    if (built.styleId && built.style) styles[built.styleId] = built.style
    return built.styleId
  }

  const num = (formatCode: string) => ({ numberFormat: { formatCode } })
  const serif = { fontFamily: CFO_FONTS.serif }
  const sans = { fontFamily: CFO_FONTS.sans }
  const thinBottom = { border: { bottom: { style: 'thin', color: CFO_PALETTE.border } } }
  const subTop = { border: { top: { style: 'thin', color: CFO_PALETTE.borderStrong } } }
  const totalTop = { border: { top: { style: 'medium', color: CFO_PALETTE.ink } } }

  const roleFormats: Record<CfoWorkbookStyleRole, Record<string, unknown>> = {
    coverTitle: { ...serif, fontSize: 30, textColor: CFO_PALETTE.ink, verticalAlign: 'middle' },
    coverSubtitle: { ...sans, fontSize: 12, textColor: CFO_PALETTE.subtle, verticalAlign: 'middle' },
    coverMeta: { ...sans, fontSize: 10, textColor: CFO_PALETTE.subtle, verticalAlign: 'middle' },
    brandBand: { ...sans, bold: true, fontSize: 11, textColor: CFO_PALETTE.brass, verticalAlign: 'middle', align: 'left' },
    legend: { ...sans, fontSize: 10, textColor: CFO_PALETTE.subtle, verticalAlign: 'middle' },
    sectionHeader: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.faint, verticalAlign: 'middle', align: 'left' },
    columnHeader: { ...sans, fontSize: 10, textColor: CFO_PALETTE.headerText, align: 'left', verticalAlign: 'middle', ...thinBottom },
    columnHeaderRight: { ...sans, fontSize: 10, textColor: CFO_PALETTE.headerText, align: 'right', verticalAlign: 'middle', ...thinBottom },
    unitsRow: { ...sans, fontSize: 9, textColor: CFO_PALETTE.faint, align: 'right', verticalAlign: 'middle' },
    rowLabel: { ...sans, fontSize: 10, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle' },
    rowLabelIndent: { ...sans, fontSize: 10, textColor: CFO_PALETTE.subtle, align: 'left', verticalAlign: 'middle', indent: 1 },
    note: { ...sans, fontSize: 9, italic: true, textColor: CFO_PALETTE.faint, align: 'left', verticalAlign: 'middle', wrapText: true },
    inputText: { ...sans, fontSize: 10, textColor: CFO_PALETTE.inputBlue, backgroundColor: CFO_PALETTE.inputFill, align: 'left', verticalAlign: 'middle' },
    inputNumber: { ...sans, fontSize: 10, bold: true, textColor: CFO_PALETTE.inputBlue, backgroundColor: CFO_PALETTE.inputFill, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    inputPercent: { ...sans, fontSize: 10, bold: true, textColor: CFO_PALETTE.inputBlue, backgroundColor: CFO_PALETTE.inputFill, align: 'right', verticalAlign: 'middle', ...num(fmt.percent) },
    inputDays: { ...sans, fontSize: 10, bold: true, textColor: CFO_PALETTE.inputBlue, backgroundColor: CFO_PALETTE.inputFill, align: 'right', verticalAlign: 'middle', ...num(fmt.days) },
    formula: { ...sans, fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    formulaPercent: { ...sans, fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', verticalAlign: 'middle', ...num(fmt.percent) },
    formulaMuted: { ...sans, fontSize: 10, textColor: CFO_PALETTE.subtle, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    link: { ...sans, fontSize: 10, textColor: CFO_PALETTE.linkGreen, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    actualNumber: { ...sans, fontSize: 10, textColor: CFO_PALETTE.actualText, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    forecastNumber: { ...sans, fontSize: 10, textColor: CFO_PALETTE.ink, backgroundColor: CFO_PALETTE.forecastFill, align: 'right', verticalAlign: 'middle', ...num(fmt.currency) },
    subtotal: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle', ...subTop },
    subtotalNumber: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', verticalAlign: 'middle', ...subTop, ...num(fmt.currency) },
    total: { ...serif, bold: true, fontSize: 11, textColor: CFO_PALETTE.totalText, align: 'left', verticalAlign: 'middle', ...totalTop },
    totalNumber: { ...serif, bold: true, fontSize: 11, textColor: CFO_PALETTE.totalText, align: 'right', verticalAlign: 'middle', ...totalTop, ...num(fmt.currency) },
    kpiLabel: { ...sans, bold: true, fontSize: 9, textColor: CFO_PALETTE.faint, align: 'left', verticalAlign: 'top' },
    kpiValue: { ...serif, fontSize: 18, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle' },
    kpiValueLarge: { ...serif, fontSize: 30, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle' },
    kpiUnit: { ...sans, fontSize: 9, textColor: CFO_PALETTE.faint, align: 'left', verticalAlign: 'top' },
    caseHigh: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.okText, backgroundColor: CFO_PALETTE.okFill, align: 'center', verticalAlign: 'middle' },
    caseExpected: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.subtle, backgroundColor: CFO_PALETTE.subtotalFill, align: 'center', verticalAlign: 'middle' },
    caseLow: { ...sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.dangerText, backgroundColor: CFO_PALETTE.dangerFill, align: 'center', verticalAlign: 'middle' },
  }

  const idByRole = {} as Record<CfoWorkbookStyleRole, string | null>
  for (const role of Object.keys(roleFormats) as CfoWorkbookStyleRole[]) {
    idByRole[role] = register(roleFormats[role])
  }

  return {
    styles,
    id: (role) => idByRole[role],
    custom: (format) => register(format),
    numberFormats: fmt,
  }
}
